import { View } from 'react-native';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { TBaseButtonProps } from '@photosi/rn-components/buttons/base-button/BaseButton.types';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import Animated from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { stylesV3 } from './ToolbarTileV3.styles';
import { Icon } from '@photosi/rn-components/icon/Icon';

export type ToolbarButtonProps = {
  iconName: ICON_NAMES;
  isV3?: boolean;
  text: string;
} & Omit<TBaseButtonProps, 'children'>;

export const ToolbarButton = observer(
  ({
    iconName,
    style,
    testID,
    focusId,
    isV3,
    text,
    accessible = true,
    accessibilityLabel,
    accessibilityRole,
    ...buttonProps
  }: ToolbarButtonProps) => {
    const { i18n } = useStore();

    const styles = stylesV3;

    return (
      <BaseButton
        testID={testID}
        focusId={focusId ?? testID}
        style={mergeStyles([styles.button, style])}
        focusStyle={styles.buttonFocus}
        hoverStyle={styles.buttonHover}
        disabledStyle={styles.buttonDisabled}
        innerStyle={styles.buttonInner}
        accessible={accessible}
        accessibilityLabel={accessibilityLabel ?? text ?? `toolbar button ${focusId ?? testID}`}
        accessibilityRole={accessibilityRole ?? 'button'}
        {...buttonProps}
      >
        {() => (
          <>
            <View style={styles.iconContainer}>
              <Icon
                name={iconName}
                width={styles.icon.width as number}
                height={styles.icon.height as number}
                color={(styles.icon as any).color}
              />
            </View>
            <View style={styles.toolDetailsContainer}>
              <Animated.View style={[styles.toolDetails]}>
                {!!text && (
                  <TextLato
                    style={mergeStyles(styles.toolDetailsText, styles.toolDetailsTitle)}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {i18n.t(text)}
                  </TextLato>
                )}
              </Animated.View>
            </View>
          </>
        )}
      </BaseButton>
    );
  },
);

ToolbarButton.displayName = 'ToolbarButton';
